"use client"
import React, { useState, useEffect } from "react";
import { useTranslations } from 'next-intl';
import config from "@/config";
import ButtonCheckout from "@/components/ButtonCheckout";
import OneTimePlan from "./OneTimePlan";

const Pricing = () => {
  const t = useTranslations('Pricing');
  const [isYearly, setIsYearly] = useState(true);
  const allPlans = config.stripe.plans;
  const oneTimePlan = allPlans.find(plan => plan.isOneTime);
  const plans = allPlans.filter(plan => !plan.isOneTime);
  const popularIndex = config.stripe.popularIndex;
  const [environment, setEnvironment] = useState('development');

  useEffect(() => {
    setEnvironment(process.env.NODE_ENV === 'production' ? 'production' : 'development');
  }, []);

  const gridCols = plans.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2';

  // 计算年费节省的月数和金额
  const calculateSavings = (plan) => {
    const monthlyTotal = plan.price * 12;
    const yearlyTotal = plan.yearlyPrice * 12;
    const savedAmount = monthlyTotal - yearlyTotal;
    const savedMonths = Math.round((savedAmount / plan.price) * 10) / 10; // 保留一位小数
    return {
      yearlyTotal: yearlyTotal.toFixed(1),
      savedAmount: savedAmount.toFixed(1),
      savedMonths
    };
  };

  // 获取特定计划的功能列表
  const getPlanFeatures = (planName, isYearlyPlan) => {
    try {
      // 对于一次性购买计划，使用固定的 features 字段
      if (planName === 'Onetime') {
        const planData = t.raw(`plans.${planName}`);
        if (planData && planData.features && Array.isArray(planData.features)) {
          return planData.features.map(feature => ({
            name: feature.name,
            included: feature.included === true || feature.included === 'true'
          }));
    }
    return [];
      }
      
      // 对于订阅计划，根据年费/月费使用不同的特性
      const featureType = isYearlyPlan ? 'yearlyFeatures' : 'monthlyFeatures';
      const planData = t.raw(`plans.${planName}`);
      
      if (planData && planData[featureType] && Array.isArray(planData[featureType])) {
        return planData[featureType].map(feature => ({
          name: feature.name,
          included: feature.included === true || feature.included === 'true'
        }));
      }
      
      return [];
    } catch (error) {
      console.error(`Error getting features for ${planName}:`, error);
      return [];
    }
  };

  return (
    <section className="py-12 md:py-16 bg-black relative overflow-hidden" id="pricing">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl overflow-hidden">
        <div className="max-w-3xl mx-auto text-center mb-6 md:mb-10">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text drop-shadow-[0_0_10px_rgba(168,85,247,0.3)]">
              {t('title')}
            </span>
          </h2>
          <p className="mt-2 text-sm md:text-base text-gray-300 max-w-2xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* 价格切换器 */}
        <div className="flex justify-center mb-6 md:mb-10">
          <div className="inline-flex p-1 bg-black/60 backdrop-blur-md rounded-xl border-2 border-green-400/30">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-3 md:px-5 py-1.5 md:py-2 text-sm rounded-lg transition-all duration-300 ${
                !isYearly 
                  ? 'bg-gradient-to-r from-green-400 to-cyan-400 text-white shadow-[0_0_10px_rgba(168,85,247,0.3)]' 
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {t('monthlyPlan')}
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-3 md:px-5 py-1.5 md:py-2 text-sm rounded-lg transition-all duration-300 ${
                isYearly 
                  ? 'bg-gradient-to-r from-green-400 to-cyan-400 text-white shadow-[0_0_10px_rgba(168,85,247,0.3)]' 
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {t('yearlyPlan')}
              <span className="ml-1 text-xs bg-black/60 text-green-300 px-1.5 py-0.5 rounded-full border border-green-500/50">
                {t('savePercent')}
              </span>
            </button>
          </div>
        </div>

        {/* 价格卡片 */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${plans.length >= 3 ? 'lg:grid-cols-3' : ''} gap-4 md:gap-6 max-w-6xl mx-auto`}>
          {plans.map((plan, index) => {
            const { yearlyTotal, savedAmount, savedMonths } = calculateSavings(plan);
            const features = getPlanFeatures(plan.name, isYearly);
            return (
            <div key={plan.name} className="relative transition-all duration-300 hover:-translate-y-1 hover:scale-[1.01] group">
              {index === popularIndex && (
                <div className="absolute -top-4 right-4 z-50">
                  <div className="bg-gradient-to-r from-green-400 to-cyan-400 text-white text-xs font-bold px-3 py-0.5 rounded-t-lg shadow-[0_0_10px_rgba(168,85,247,0.3)]">
                    {t('mostPopular')}
                  </div>
                </div>
              )}
              <div className={`h-full p-4 md:p-6 rounded-xl backdrop-blur-md transition-all ${
                index === popularIndex 
                  ? 'bg-black/80 border-2 border-green-400/40 shadow-[0_0_20px_rgba(168,85,247,0.3)]' 
                  : 'bg-black/60 border-2 border-green-400/20 shadow-md group-hover:shadow-[0_0_20px_rgba(168,85,247,0.2)] group-hover:border-green-400/40'
              }`}>
                <h3 className="text-lg md:text-xl font-bold text-white mb-1">{t(`plans.${plan.name}.name`)}</h3>
                <p className="text-gray-200 text-xs md:text-sm mb-4">{t(`plans.${plan.name}.description`)}</p>
                
                <div className="mb-4">
                  <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text drop-shadow-[0_0_5px_rgba(168,85,247,0.3)]">
                    ${isYearly ? plan.yearlyPrice : plan.price}
                    <span className="text-sm text-gray-200 ml-1">{t('perMonth')}</span>
                  </p>
                  {isYearly && plan.price !== 0 && (
                    <>
                      <p className="text-xs text-gray-200 mt-0.5">{t('billedAnnually')}</p>
                      <p className="text-xs md:text-sm text-gray-200 mt-1 font-medium">
                        ${yearlyTotal} <span className="text-xs text-gray-200">{t('yearlyTotalLabel')}</span>
                      </p>
                      <p className="text-xs text-green-300 mt-0.5 drop-shadow-[0_0_5px_rgba(168,85,247,0.2)]">
                        {t('savedAmountLabel')} ${savedAmount} {t('freeMonthsLabel', { months: savedMonths })}
                      </p>
                    </>
                  )}
                </div>

                <div className="mb-4">
                  {plan.price === 0 ? (
                    <button disabled className="w-full py-2 rounded-lg bg-gray-900 text-gray-400 border-2 border-gray-800">
                      {t('freePlan')}
                    </button>
                  ) : (
                    <ButtonCheckout
                      plan={plan}
                      isYearly={isYearly}
                      className={`w-full py-2 rounded-lg font-medium transition-all duration-300 hover:scale-[1.02] ${
                        index === popularIndex
                          ? 'bg-gradient-to-r from-green-400 to-cyan-400 hover:from-green-500 hover:to-cyan-500 text-white shadow-[0_0_15px_rgba(168,85,247,0.3)] hover:shadow-[0_0_20px_rgba(168,85,247,0.5)]'
                          : 'bg-black border-2 border-green-400/40 text-white hover:bg-green-400/10 hover:border-green-400/70'
                      }`}
                    >
                      {t('buyNow')}
                    </ButtonCheckout>
                  )}
                </div>

                <ul className="space-y-2.5">
                  {features.map((feature, featureIndex) => (
                    <li key={featureIndex} className={`flex items-start p-2 rounded-lg transition-all duration-200 ${
                      feature.included 
                        ? 'bg-green-900/10 border border-green-400/20 hover:bg-green-900/20' 
                        : 'bg-gray-900/20 border border-gray-600/20 hover:bg-gray-900/30'
                    }`}>
                      {feature.included ? (
                        <svg className="w-5 h-5 mr-3 mt-0.5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                                              ) : (
                          <svg className="w-5 h-5 mr-3 mt-0.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                      )}
                      <span className={`text-xs md:text-sm leading-relaxed ${
                        feature.included 
                          ? 'text-gray-200 font-medium' 
                          : 'text-gray-400 font-normal line-through decoration-gray-600'
                      }`}>
                        {feature.included ? (
                          feature.name.split(/(\d+(?:\.\d+)?k(?=\s)|(?<=\s)\d+(?=\s+VEO3))/g).map((part, i) => {
                            // 检查是否是需要高亮的数字部分
                            if (/^\d+(?:\.\d+)?k$|^\d+$/.test(part)) {
                            return (
                              <span key={i} className="font-bold bg-gradient-to-r from-cyan-400 to-green-400 text-transparent bg-clip-text drop-shadow-[0_0_5px_rgba(236,72,153,0.4)]">
                                {part}
                              </span>
                            );
                          }
                          return part;
                          })
                        ) : (
                          feature.name
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )})}
        </div>

        {/* 一次性购买计划 */}
        {oneTimePlan && <OneTimePlan plan={oneTimePlan} />}

        {/* 功能对比表格 */}
        <div className="mt-12 md:mt-16 mb-8 md:mb-12 max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
              <span className="bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text drop-shadow-[0_0_10px_rgba(168,85,247,0.3)]">
                {t('featureComparisonTitle') || 'Feature Comparison'}
              </span>
            </h3>
            <p className="text-sm text-gray-400">
              {t('featureComparisonSubtitle') || 'Compare features across different plans'}
            </p>
          </div>

          {/* 桌面端表格 - 在移动端隐藏 */}
          <div className="hidden md:block overflow-hidden rounded-xl border-2 border-green-400/30 shadow-[0_0_20px_rgba(168,85,247,0.15)] transition-all hover:shadow-[0_0_30px_rgba(168,85,247,0.25)]">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-black/90 to-black/80 backdrop-blur-md border-b-2 border-green-400/30">
                  <th className="text-left p-4 text-gray-300 font-medium">{t('featureLabel') || 'Feature'}</th>
                  {plans.map(plan => (
                    <th key={plan.name} className={`p-4 text-center ${
                      plan.name === plans[popularIndex].name 
                        ? 'bg-gradient-to-b from-green-900/30 to-green-900/10 border-t-2 border-green-400/40' 
                        : ''
                    }`}>
                      <span className={`text-white font-bold ${
                        plan.name === plans[popularIndex].name 
                          ? 'bg-gradient-to-r from-green-300 to-cyan-300 text-transparent bg-clip-text drop-shadow-[0_0_10px_rgba(168,85,247,0.5)]' 
                          : ''
                      }`}>{t(`plans.${plan.name}.name`)}</span>
                      {plan.name === plans[popularIndex].name && (
                        <div className="text-xs text-green-300 mt-1 drop-shadow-[0_0_5px_rgba(168,85,247,0.3)] z-50">
                          {t('mostPopular')}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* 积分数量 */}
                <tr className="border-b border-green-400/20">
                  <td className="p-4 text-gray-300 border-r border-green-400/20">{isYearly ? 'Credits per year' : 'Credits per month'}</td>
                  {plans.map(plan => (
                    <td key={plan.name} className={`p-4 text-center border-l border-r border-green-400/20 ${
                      plan.name === plans[popularIndex].name 
                        ? 'bg-green-900/10' 
                        : 'bg-black/70 backdrop-blur-md'
                    }`}>
                      <span className={`font-bold ${
                        plan.name === plans[popularIndex].name 
                          ? 'bg-gradient-to-r from-cyan-400 to-green-400 text-transparent bg-clip-text drop-shadow-[0_0_5px_rgba(236,72,153,0.4)]' 
                          : 'text-gray-300'
                      }`}>
                        {isYearly 
                          ? (plan.name === 'Basic' ? '6k' : plan.name === 'Plus' ? '18k' : '60k')
                          : (plan.name === 'Basic' ? '500' : plan.name === 'Plus' ? '1.5k' : '5k')
                        }
                      </span>
                    </td>
                  ))}
                </tr>
                
                {/* 处理速度 */}
                <tr className="border-b border-green-400/20">
                  <td className="p-4 text-gray-300 border-r border-green-400/20">{t('processingSpeedLabel') || 'Processing speed'}</td>
                  {plans.map(plan => (
                    <td key={plan.name} className={`p-4 text-center border-l border-r border-green-400/20 ${
                      plan.name === plans[popularIndex].name 
                        ? 'bg-green-900/10' 
                        : 'bg-black/70 backdrop-blur-md'
                    }`}>
                      <span className={`font-medium ${
                        plan.name === plans[popularIndex].name 
                          ? 'text-green-300 drop-shadow-[0_0_5px_rgba(168,85,247,0.2)]' 
                          : 'text-gray-400'
                      }`}>
                        {plan.name === 'Basic' ? 'Standard' : plan.name === 'Plus' ? 'Priority' : 'Fastest'}
                      </span>
                    </td>
                  ))}
                </tr>
                
                {/* 水印 */}
                <tr className="border-b border-green-400/20">
                  <td className="p-4 text-gray-300 border-r border-green-400/20">Watermark</td>
                  {plans.map(plan => (
                    <td key={plan.name} className={`p-4 text-center border-l border-r border-green-400/20 ${
                      plan.name === plans[popularIndex].name 
                        ? 'bg-green-900/10' 
                        : 'bg-black/70 backdrop-blur-md'
                    }`}>
                      <div className="flex justify-center">
                        <div className="w-5 h-5 bg-gradient-to-r from-green-400 to-cyan-400 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 mt-1">No watermark</div>
                    </td>
                  ))}
                </tr>
                
                {/* 商业许可 */}
                <tr className={`border-b border-green-400/20 ${!isYearly ? 'bg-red-900/5' : ''}`}>
                  <td className="p-4 text-gray-300 border-r border-green-400/20">
                    Commercial license
                    {!isYearly && (
                      <div className="text-xs text-green-400 font-medium mt-1">
                        ⭐ Available with yearly plans
                      </div>
                    )}
                  </td>
                  {plans.map(plan => (
                    <td key={plan.name} className={`p-4 text-center border-l border-r border-green-400/20 ${
                      plan.name === plans[popularIndex].name 
                        ? 'bg-green-900/10' 
                        : 'bg-black/70 backdrop-blur-md'
                    }`}>
                      <div className="flex justify-center">
                        {isYearly ? (
                          <svg className="w-5 h-5 text-green-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                      <div className={`text-xs mt-1 ${isYearly ? 'text-green-400 font-medium' : 'text-red-400'}`}>
                        {isYearly ? '✓ Included' : '✗ Not included'}
                      </div>
                    </td>
                  ))}
                </tr>
                
                {/* 优先支持 */}
                <tr className={`hover:bg-green-900/5 transition-colors ${!isYearly ? 'bg-red-900/5' : ''}`}>
                  <td className="p-4 text-gray-300">
                    Priority support
                    {!isYearly && (
                      <div className="text-xs text-green-400 font-medium mt-1">
                        ⭐ Available with yearly plans
                      </div>
                    )}
                  </td>
                  {plans.map(plan => (
                    <td key={plan.name} className={`p-4 text-center ${
                      plan.name === plans[popularIndex].name 
                        ? 'bg-green-900/10 border-l border-r border-t border-green-400/20 rounded-b-lg' 
                        : 'bg-black/70 backdrop-blur-md'
                    }`}>
                      <div className="flex justify-center">
                        {isYearly ? (
                          <svg className="w-5 h-5 text-green-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                      <div className={`text-xs mt-1 ${isYearly ? 'text-green-400 font-medium' : 'text-red-400'}`}>
                        {isYearly ? '✓ Included' : '✗ Not included'}
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* 移动端折叠表格 - 在桌面端隐藏 */}
          <div className="md:hidden space-y-4">
            {plans.map((plan, index) => (
              <div 
                key={plan.name} 
                className={`rounded-xl p-4 backdrop-blur-md transition-all hover:-translate-y-0.5 ${
                  index === popularIndex 
                    ? 'bg-gradient-to-b from-black/90 to-black/80 border-2 border-green-400/40 shadow-[0_0_20px_rgba(168,85,247,0.3)]' 
                    : 'bg-black/70 border-2 border-green-400/20 shadow-md hover:shadow-[0_0_15px_rgba(168,85,247,0.2)] hover:border-green-400/30'
                }`}
              >
                <div className="mb-3 flex justify-between items-center">
                  <h4 className={`text-lg font-bold ${
                    index === popularIndex 
                      ? 'bg-gradient-to-r from-green-300 to-cyan-300 text-transparent bg-clip-text drop-shadow-[0_0_10px_rgba(168,85,247,0.5)]' 
                      : 'text-white'
                  }`}>{t(`plans.${plan.name}.name`)}</h4>
                  {index === popularIndex && (
                    <span className="text-xs bg-gradient-to-r from-green-400 to-cyan-400 text-white px-2 py-0.5 rounded-full shadow-[0_0_10px_rgba(168,85,247,0.3)] z-50">
                      {t('mostPopular')}
                    </span>
                  )}
                </div>
                
                <ul className="space-y-2.5">
                  <li className="flex justify-between items-center py-1 border-b border-green-400/10">
                    <span className="text-gray-400 text-sm">{isYearly ? 'Credits per year' : 'Credits per month'}</span>
                    <span className="text-sm">
                      <span className="font-bold bg-gradient-to-r from-cyan-400 to-green-400 text-transparent bg-clip-text drop-shadow-[0_0_5px_rgba(236,72,153,0.4)]">
                        {isYearly 
                          ? (plan.name === 'Basic' ? '6k' : plan.name === 'Plus' ? '18k' : '60k')
                          : (plan.name === 'Basic' ? '500' : plan.name === 'Plus' ? '1.5k' : '5k')
                        }
                      </span>
                    </span>
                  </li>
                  <li className="flex justify-between items-center py-1 border-b border-green-400/10">
                    <span className="text-gray-400 text-sm">{t('processingSpeedLabel') || 'Processing speed'}</span>
                    <span className="text-gray-300 text-sm font-medium">
                      {plan.name === 'Basic' ? 'Standard' : plan.name === 'Plus' ? 'Priority' : 'Fastest'}
                    </span>
                  </li>
                  <li className="flex justify-between items-center py-1 border-b border-green-400/10">
                    <span className="text-gray-400 text-sm">Watermark</span>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gradient-to-r from-green-400 to-cyan-400 rounded-full flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-300 text-sm">No watermark</span>
                    </div>
                  </li>
                  <li className={`flex justify-between items-center py-1 border-b border-green-400/10 ${!isYearly ? 'bg-red-900/5 rounded px-2' : ''}`}>
                    <div className="flex flex-col">
                      <span className="text-gray-400 text-sm">Commercial license</span>
                      {!isYearly && (
                        <span className="text-xs text-green-400 font-medium">⭐ Available yearly</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isYearly ? (
                        <svg className="w-4 h-4 text-green-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={`text-sm ${isYearly ? 'text-green-400 font-medium' : 'text-red-400'}`}>
                        {isYearly ? '✓ Included' : '✗ Not included'}
                      </span>
                    </div>
                  </li>
                  <li className={`flex justify-between items-center py-1 ${!isYearly ? 'bg-red-900/5 rounded px-2' : ''}`}>
                    <div className="flex flex-col">
                      <span className="text-gray-400 text-sm">Priority support</span>
                      {!isYearly && (
                        <span className="text-xs text-green-400 font-medium">⭐ Available yearly</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isYearly ? (
                        <svg className="w-4 h-4 text-green-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={`text-sm ${isYearly ? 'text-green-400 font-medium' : 'text-red-400'}`}>
                        {isYearly ? '✓ Included' : '✗ Not included'}
                    </span>
                    </div>
                  </li>
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* 自定义方案引导 */}
        <div className="mt-8 md:mt-10 text-center">
          <p className="text-gray-200 text-sm mb-2">{t('customPlanQuestion')}</p>
          <a 
            href="mailto:support@wan22.io" 
            className="group inline-flex items-center px-4 py-2 rounded-full bg-black/60 border border-green-400/30 hover:border-green-400/50 hover:bg-green-400/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(168,85,247,0.2)]"
          >
            <svg 
              className="w-4 h-4 mr-2 text-green-400 group-hover:text-green-300 transition-colors" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
              />
            </svg>
            <span className="text-sm md:text-base font-medium bg-gradient-to-r from-green-400 to-cyan-400 text-transparent bg-clip-text group-hover:from-green-300 group-hover:to-cyan-300 transition-all">
              {t('customPlanCTA')}
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Pricing;